import classNames from 'classnames';
import _ from 'lodash';
import React, { useMemo } from 'react';
import FadeIn from 'react-fade-in';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Feature, useFeature } from '../../hooks/use-feature';
import { useStorage } from '../../hooks/use-storage';
import { layoutDefaults } from '../../utils/layout-defaults';
import { parseDefaultTextColor } from '../../utils/manager/layout-manager';
import {
  filterByUser,
  isPayable,
  parseTip,
  parseTotal,
} from '../../utils/manager/order-manager';
import { parseValue } from '../../utils/manager/price-manager';

function Summary({ selectedOrders }) {
  const { spotRef, ordersRef, userRef, menuLayout, menuInfo, orders } =
    useStorage();
  const { isFeatureEnabled } = useFeature();
  const classes = useStyles(menuLayout);
  const { t } = useTranslation();
  const symbol = menuInfo.i18n?.currency?.symbol;
  const tipEnabled = isFeatureEnabled(Feature.ON_SITE_TIP);

  function buildPhrase(count, showSelected, isPartial) {
    return showSelected
      ? t(
          spotRef.current?.prepaid
            ? 'bill.summary.selected-item'
            : 'bill.summary.selected-order',
          { count }
        )
      : t(`bill.summary.${isPartial ? 'sub-total' : 'total'}`);
  }

  const subTotalTitle = useMemo(() => {
    const showSelected = selectedOrders.length > 0;
    return buildPhrase(selectedOrders.length, showSelected, true);
  }, [selectedOrders, spotRef.current]);

  const subTotalValue = useMemo(
    () =>
      parseTotal(
        selectedOrders.length > 0 ? selectedOrders : ordersRef.current
      ),
    [ordersRef.current, selectedOrders]
  );

  const tipTitle = useMemo(() => t('bill.summary.optional-service'), []);

  const tipValue = useMemo(
    () =>
      parseTip(
        _.isEmpty(selectedOrders) ? orders : selectedOrders,
        tipEnabled,
        menuInfo.on_site?.tip?.suggestion
      ),
    [orders, selectedOrders, menuInfo.on_site?.tip]
  );

  const totalTitle = useMemo(() => {
    const showSelected = !tipEnabled && selectedOrders.length > 0;
    return buildPhrase(selectedOrders.length, showSelected, false);
  }, [selectedOrders, menuInfo.on_site?.tip, spotRef.current]);

  const pendentUserOrdersLength = useMemo(
    () =>
      filterByUser(ordersRef.current, userRef.current).filter(
        (o) =>
          (isPayable(o) || o.payment?.status === 'paying') &&
          o.session?.expires_at == null
      )?.length ?? 0,
    [ordersRef.current, userRef.current]
  );

  const showPendentAlertLabel = useMemo(
    () => pendentUserOrdersLength > 0 && spotRef.current?.prepaid,
    [pendentUserOrdersLength, spotRef.current]
  );

  return (
    <FadeIn>
      <div className={classes.containerTop}>
        <div className={classes.column}>
          {tipEnabled && (
            <>
              <div className={classNames(classes.labelContainer, classes.line)}>
                {subTotalTitle} {symbol}
              </div>
              <div className={classNames(classes.labelContainer, classes.line)}>
                {tipTitle} {symbol}
              </div>
            </>
          )}
          <div className={classNames(classes.labelContainer, classes.line)}>
            {totalTitle} {symbol}
          </div>
        </div>
        <div className={classes.column}>
          {tipEnabled && (
            <>
              <div className={classNames(classes.valueContainer, classes.line)}>
                {parseValue(subTotalValue)}
              </div>
              <div className={classNames(classes.valueContainer, classes.line)}>
                {parseValue(tipValue)}
              </div>
            </>
          )}
          <div className={classNames(classes.valueContainer, classes.line)}>
            {parseValue(subTotalValue + tipValue)}
          </div>
        </div>
      </div>
      {showPendentAlertLabel && (
        <div className={classes.containerBottom}>
          <span className={classes.labelContainer}>
            {t('summary.order')}&nbsp;
          </span>
          {t('summary.not-sent').toUpperCase()}
          <span className={classes.labelContainer}>
            &nbsp;{t('summary.waiting-payment')}
          </span>
        </div>
      )}
    </FadeIn>
  );
}

const useStyles = createUseStyles({
  containerTop: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignContent: 'center',
    fontSize: layoutDefaults.fontSize,
    border: (layout) =>
      `1px dashed ${parseDefaultTextColor(
        layout,
        layoutDefaults.smallOpacity
      )}`,
    marginTop:
      layoutDefaults.biggerVerticalMargin + layoutDefaults.smallVerticalMargin,
    marginBottom: layoutDefaults.smallVerticalMargin,
    paddingTop: layoutDefaults.smallerVerticalMargin,
    paddingBottom: layoutDefaults.smallVerticalMargin * 1.4,

    borderRadius: layoutDefaults.radius,

    // border: '1px solid yellow',
  },
  containerBottom: {
    textAlign: 'right',
    fontSize: layoutDefaults.fontSize,
    border: (layout) =>
      `1px dashed ${parseDefaultTextColor(
        layout,
        layoutDefaults.smallOpacity
      )}`,
    // marginTop:
    //   layoutDefaults.biggerVerticalMargin + layoutDefaults.smallVerticalMargin,
    marginBottom: layoutDefaults.smallVerticalMargin,
    paddingTop: layoutDefaults.verticalMargin - 2,
    paddingBottom: layoutDefaults.smallVerticalMargin * 1.4,
    paddingLeft: layoutDefaults.horizontalMargin,
    paddingRight: layoutDefaults.horizontalMargin,

    borderRadius: layoutDefaults.radius,

    // border: '1px solid yellow',
  },
  column: {
    // border: "1px solid pink",
  },
  line: {
    marginTop: layoutDefaults.smallVerticalMargin,
  },
  labelContainer: {
    textAlign: 'right',
    textTransform: 'uppercase',
    opacity: layoutDefaults.opacity,
    // marginTop: layoutDefaults.verticalMargin,
    // border: '1px solid white',
  },
  valueContainer: {
    // border: '1px solid blue',
    textAlign: 'right',
    // verticalAlign: 'center',
    marginRight: layoutDefaults.horizontalMargin,
    marginLeft: layoutDefaults.smallerHorizontalMargin,
  },
});

export { Summary };
